import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';

import {
  MaximizeIcon, PlusIcon, MinusIcon, LocationNavigationIcon,
} from '@vlabs/icons';
import mapStyles from '@vlabs/shared/components/filters-panel/geolocation/map-wrapper/MapWrapper.module.sass';
import { ButtonScrimGroup } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import cn from 'classnames';
import type { Map as TMap } from 'ol';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Draw, { createBox } from 'ol/interaction/Draw';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { fromLonLat, toLonLat } from 'ol/proj';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';

import { useEventListFiltersTranslation } from '../../../i18n';
import { useEventListFilters } from '../../../useEventListFilters';
import st from '../../popovers.module.sass';

const mapSt = mapStyles as Record<string, string>;

const defaultCenter = [4187399, 7508881];
const defaultZoom = 10;

type TAction = 'increase' | 'decrease' | 'geolocation';

function MapView(): React.JSX.Element {
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { t } = useEventListFiltersTranslation();
  const { filters, updateFilters } = useEventListFilters();

  const mapTargetElement = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<TMap | null>(null);
  const sourceRef = useRef<VectorSource | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const toggleFullScreen = () => setIsFullScreen((p) => !p);

  const handleGetUserLocation = useCallback(async () => (
    new Promise<GeolocationPosition | null>((resolve) => {
      if (!navigator.geolocation) {
        toast.error(t('map_error_unsupported'));
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        () => {
          toast.error(t('map_error_unavailable'));
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 5000 },
      );
    })
  ), [t]);

  const handleChangeMapView = useCallback(async (action: TAction) => {
    if (!mapRef.current) return;

    const currentView = mapRef.current.getView();
    const currentCenter = currentView.getCenter();
    const currentZoom = currentView.getZoom();

    if (!currentCenter || !currentZoom) return;

    if (action === 'increase') currentView.setZoom(currentZoom + 1);
    if (action === 'decrease') currentView.setZoom(currentZoom - 1);
    if (action === 'geolocation') {
      const location = await handleGetUserLocation();
      if (location) {
        const { latitude, longitude } = location.coords;
        currentView.setCenter(fromLonLat([longitude, latitude]));
        currentView.setZoom(14);
      } else {
        currentView.setCenter(defaultCenter);
        currentView.setZoom(defaultZoom);
      }
    }
  }, [handleGetUserLocation]);

  const changeMapView = (action: TAction) => {
    handleChangeMapView(action).catch(() => undefined);
  };

  useEffect(() => {
    if (!mapRef.current || !mapTargetElement.current) return;

    mapRef.current.setTarget(mapTargetElement.current);

    if (isFullScreen) {
      requestAnimationFrame(() => {
        mapRef.current?.updateSize();
        mapRef.current?.render();
      });
    }
  }, [isFullScreen]);

  useEffect(() => {
    if (mapRef.current) return undefined;

    const source = new VectorSource({ wrapX: false });
    const draw = new Draw({
      source,
      type: 'Circle',
      geometryFunction: createBox(),
    });

    const raster = new TileLayer({ source: new OSM() });
    const vector = new VectorLayer({ source });

    const $map = new Map({
      target: mapTargetElement.current ?? '',
      controls: [],
      layers: [raster, vector],
      view: new View({
        center: defaultCenter,
        zoom: defaultZoom,
      }),
    });
    mapRef.current = $map;
    sourceRef.current = source;
    $map.addInteraction(draw);

    const {
      origin_latitude: originLatitude,
      origin_longitude: originLongitude,
      latitude_delta: latitudeDelta,
      longitude_delta: longitudeDelta,
    } = filtersRef.current;

    const isNumber = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

    if (
      isNumber(originLatitude) && isNumber(originLongitude)
      && isNumber(latitudeDelta) && isNumber(longitudeDelta)
    ) {
      const lat1 = originLatitude - latitudeDelta;
      const lat2 = originLatitude + latitudeDelta;
      const lon1 = originLongitude - longitudeDelta;
      const lon2 = originLongitude + longitudeDelta;

      const coords = [
        fromLonLat([lon1, lat1]),
        fromLonLat([lon1, lat2]),
        fromLonLat([lon2, lat2]),
        fromLonLat([lon2, lat1]),
        fromLonLat([lon1, lat1]),
      ];

      const polygon = new Polygon([coords]);
      const feature = new Feature(polygon);
      source.addFeature(feature);
    }

    draw.on('drawstart', () => {
      source.clear();
    });

    source.on('addfeature', (e) => {
      const { feature } = e;
      // @ts-expect-error у прототипа есть этот метод
      const coordsList = feature?.getGeometry()?.getCoordinates()[0] as number[][];
      const firstPoint = coordsList[0];
      const thirdPoint = coordsList[2];
      if (!firstPoint || !thirdPoint) return;

      const [lon1 = 0, lat1 = 0] = toLonLat(firstPoint);
      const [lon2 = 0, lat2 = 0] = toLonLat(thirdPoint);

      const lngDelta = (lon2 - lon1) / 2;
      const latDelta = (lat2 - lat1) / 2;

      const centerLon = lon1 + lngDelta;
      const centerLat = lat1 + latDelta;

      updateFilters({
        origin_latitude: centerLat,
        origin_longitude: centerLon,
        latitude_delta: latDelta,
        longitude_delta: lngDelta,
      });
    });

    return () => $map.setTarget('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isZoneEmpty = filters.origin_latitude === undefined
      && filters.origin_longitude === undefined
      && filters.latitude_delta === undefined
      && filters.longitude_delta === undefined;

    if (isZoneEmpty) sourceRef.current?.clear();
  }, [
    filters.origin_latitude,
    filters.origin_longitude,
    filters.latitude_delta,
    filters.longitude_delta,
  ]);

  const mapContent = (
    <React.Fragment>
      {!isFullScreen && <div className={mapSt.Title}>{t('location_map_hint')}</div>}
      <div className={cn(mapSt.Wrapper, isFullScreen && mapSt.FullScreen)}>
        <ButtonScrimGroup
          className={cn(mapSt.Button, mapSt.MaximizeButton)}
          orientation="vertical"
        >
          <IconButton
            icon={<MaximizeIcon />}
            onClick={toggleFullScreen}
          />
        </ButtonScrimGroup>
        <ButtonScrimGroup
          className={cn(mapSt.Button, mapSt.ZoomButtons)}
          orientation="vertical"
        >
          <IconButton
            icon={<PlusIcon />}
            onClick={() => changeMapView('increase')}
          />
          <IconButton
            icon={<MinusIcon />}
            onClick={() => changeMapView('decrease')}
          />
        </ButtonScrimGroup>
        <ButtonScrimGroup
          className={cn(mapSt.Button, mapSt.GeoLocationButton)}
          orientation="vertical"
        >
          <IconButton
            icon={<LocationNavigationIcon />}
            onClick={() => changeMapView('geolocation')}
          />
        </ButtonScrimGroup>
        <div
          className={mapSt.Map}
          ref={mapTargetElement}
        />
      </div>
    </React.Fragment>
  );

  if (isFullScreen) {
    const root = document.getElementById('root');
    if (root) return createPortal(mapContent, root);
  }

  return mapContent;
}

export function MapTab(): React.JSX.Element {
  return (
    <div className={st.MapTabWrapper}>
      <MapView />
    </div>
  );
}
